// =============================================================================
// components/KpiIncidents/IncidentChart.jsx
//
// Grafico de evolucao do KPI com banda PREENCHIDA P5-P95 (em vez de linhas
// tracejadas isoladas) + linha SLA horizontal + valor real grosso.
//
// Para metodo PERCENTILE: usa P5/P50/P95.
// Para metodo ZSCORE/THRESHOLD: usa Avg ± 2σ.
//
// Escala Y dinamica: calculada a partir do range real dos dados visiveis
// (kpiValue, banda, mediana, SLA) com 10% de padding. Garante que pequenas
// variacoes nao ficam esmagadas num eixo 0–200.
// =============================================================================
import { useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
	ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
	Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, Legend,
} from 'recharts';
import { TECH_COLOR } from '../../constants/kpiConstants';

function fmtDate(d) {
	if (!d) return '';
	try {
		return new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
	} catch (e) { return ''; }
}

// -----------------------------------------------------------------------------
// computeYAxisRange — escala dinamica baseada apenas em kpiValue + SLA.
//
// Justificacao: avgBaseline/p50Baseline e bandHigh/bandLow podem ter
// outliers estatisticos extremos (ex: Avg+2σ de 1.235.938 numa celula
// com Stdev alto). Inclui-los no calculo destroi a escala visual.
//
// O eixo Y centra-se no que o operador efectivamente le: a curva do
// KPI e a linha de SLA. Banda e mediana sao apenas contexto — sao
// clipped pelo allowDataOverflow={false} se sairem do dominio.
//
// Uso de reduce em vez de Math.min.apply para evitar stack overflow em
// arrays grandes (limite ~65k argumentos em alguns browsers).
// -----------------------------------------------------------------------------
function computeYAxisRange(chartData, slaThreshold, direction) {
	const values = [];

	chartData.forEach(function (h) {
		if (h.kpiValue != null && isFinite(h.kpiValue)) {
			values.push(h.kpiValue);
		}
	});
	if (slaThreshold != null && isFinite(slaThreshold)) {
		values.push(slaThreshold);
	}

	if (values.length === 0) return [0, 100];

	const dataMin = values.reduce(function (a, b) { return a < b ? a : b; }, Infinity);
	const dataMax = values.reduce(function (a, b) { return a > b ? a : b; }, -Infinity);
	const range   = dataMax - dataMin;
	const padding = Math.max(range * 0.15, 1);

	let yMin = dataMin - padding;
	let yMax = dataMax + padding;

	// Metricas TKAD sao todas >= 0
	if (yMin < 0) yMin = 0;

	// KPIs "cair e mau" tipicamente percentuais: cap em 100
	if (direction === 0 && dataMax <= 100 && yMax > 100) {
		yMax = 100;
	}

	return [Math.floor(yMin), Math.ceil(yMax)];
}

// -----------------------------------------------------------------------------
// CustomTooltip
// -----------------------------------------------------------------------------
function CustomTooltip({ active, payload, label }) {
	if (!active || !payload || !payload.length) return null;
	return (
		<Box sx={{
			bgcolor: 'rgba(255,255,255,0.98)',
			border: '1px solid #e5e7eb',
			borderRadius: 1, p: 1, boxShadow: 2,
			fontSize: '0.75rem', minWidth: 140,
		}}>
			<Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
				{label}
			</Typography>
			{payload.map(function (p) {
				if (p.value == null) return null;
				return (
					<Box key={p.dataKey} sx={{
						display: 'flex', justifyContent: 'space-between', gap: 1.5,
						color: p.color,
					}}>
						<span>{p.name}</span>
						<Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
							{Number(p.value).toFixed(3)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}

export default function IncidentChart({ incident, history, loading, height }) {
	const chartHeight = height || 240;
	const techColor   = TECH_COLOR[incident.tech] || '#6366f1';
	const method      = incident.detectionMethod || 'PERCENTILE';

	// IMPORTANTE: todos os hooks tem de ser chamados ANTES de qualquer
	// early return (rules-of-hooks). Por isso useMemo vem aqui.

	// Preparar dados — banda P5-P95 para PERCENTILE, Avg±2σ para ZSCORE
	const chartData = useMemo(function () {
		if (!history || history.length === 0) return [];

		return history.map(function (h) {
			const obj = Object.assign({}, h);
			obj.dateLabel = fmtDate(h.date);

			if (method === 'PERCENTILE') {
				obj.bandLow  = h.p5Baseline;
				obj.bandHigh = h.p95Baseline;
			} else {
				if (h.avgBaseline != null && h.stdevBaseline != null) {
					obj.bandLow  = h.avgBaseline - 2 * h.stdevBaseline;
					obj.bandHigh = h.avgBaseline + 2 * h.stdevBaseline;
					obj.median   = h.avgBaseline;
				}
			}

			return obj;
		});
	}, [history, method]);

	// Escala Y dinamica
	const yDomain = useMemo(function () {
		return computeYAxisRange(chartData, incident.slaThreshold, incident.direction);
	}, [chartData, incident.slaThreshold, incident.direction]);

	// Faixa da fase de quarentena (entre o primeiro ponto do historico e
	// o Start_Date oficial). So existe se ha pontos copiados do
	// TKAD_Pre_Incident_Tracking durante a promocao (Fix 6).
	const quarantineRange = useMemo(function () {
		if (!chartData.length || !incident.startDate) return null;
		const startLabel = fmtDate(incident.startDate);
		const firstLabel = chartData[0].dateLabel;
		if (firstLabel === startLabel) return null;
		return { x1: firstLabel, x2: startLabel };
	}, [chartData, incident.startDate]);

	// --- Early returns (apos os hooks) ---------------------------------------
	if (loading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight, gap: 1, flexDirection: 'column' }}>
				<CircularProgress size={28} />
				<Typography variant="caption" color="text.secondary">A carregar historico</Typography>
			</Box>
		);
	}

	if (!history || history.length === 0) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
				<Typography variant="body2" color="text.secondary">
					Sem historico disponivel.
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<ResponsiveContainer width="100%" height={chartHeight}>
				<ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
					<defs>
						<linearGradient id={'bandGrad-' + incident.incidentId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%"   stopColor="#94a3b8" stopOpacity={0.18} />
							<stop offset="100%" stopColor="#94a3b8" stopOpacity={0.06} />
						</linearGradient>
						<linearGradient id={'lineGrad-' + incident.incidentId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%"   stopColor={techColor} stopOpacity={1} />
							<stop offset="100%" stopColor={techColor} stopOpacity={0.85} />
						</linearGradient>
					</defs>

					<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
					<XAxis
						dataKey="dateLabel"
						tick={{ fontSize: 10, fill: '#94a3b8' }}
						stroke="#e5e7eb"
						tickLine={false}
					/>
					<YAxis
						domain={yDomain}
						tick={{ fontSize: 10, fill: '#94a3b8' }}
						stroke="#e5e7eb"
						tickLine={false}
						width={40}
						allowDataOverflow={false}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Legend
						iconSize={10}
						wrapperStyle={{ fontSize: '0.7rem', paddingTop: 4 }}
						iconType="line"
					/>

					{/* Faixa da quarentena — fundo amarelo claro entre o
					    primeiro ponto e o Start_Date. Renderiza ANTES das
					    outras layers para ficar "por baixo". */}
					{quarantineRange && (
						<ReferenceArea
							x1={quarantineRange.x1}
							x2={quarantineRange.x2}
							fill="#fef3c7"
							fillOpacity={0.55}
							stroke="none"
							label={{
								value: 'Quarentena',
								position: 'insideTopLeft',
								fontSize: 9,
								fill: '#92400e',
								fontWeight: 600,
							}}
						/>
					)}

					{/* SLA threshold — linha horizontal vermelha */}
					<ReferenceLine
						y={incident.slaThreshold}
						stroke="#dc2626"
						strokeDasharray="5 3"
						strokeWidth={1.25}
						label={{
							value: 'SLA ' + Number(incident.slaThreshold).toFixed(2),
							position: 'insideTopRight',
							fontSize: 9,
							fill: '#dc2626',
						}}
					/>

					{/* Dia em que a falha foi detectada — linha vertical ambar.
					    Marca visualmente o Start_Date do incident para o
					    operador localizar imediatamente o ponto de abertura. */}
					{incident.startDate && (
						<ReferenceLine
							x={fmtDate(incident.startDate)}
							stroke="#f59e0b"
							strokeDasharray="4 2"
							strokeWidth={1.5}
							label={{
								value: 'Aberto',
								position: 'top',
								fontSize: 9,
								fill: '#f59e0b',
								fontWeight: 600,
							}}
						/>
					)}

					{/* Banda inferior (transparente, para baseline da Area) */}
					<Area
						type="monotone"
						dataKey="bandLow"
						stroke="none"
						fill="transparent"
						stackId="band"
						legendType="none"
						name="band-low-helper"
					/>
					{/* Banda principal preenchida (delta) */}
					<Area
						type="monotone"
						dataKey="bandHigh"
						stroke="#cbd5e0"
						strokeDasharray="3 3"
						strokeWidth={1}
						fill={'url(#bandGrad-' + incident.incidentId + ')'}
						stackId="band"
						name={method === 'PERCENTILE' ? 'Banda P5-P95' : 'Banda Avg ± 2σ'}
					/>

					{/* Mediana / Avg */}
					{method === 'PERCENTILE' ? (
						<Line type="monotone" dataKey="p50Baseline"
							name="Mediana (P50)"
							stroke="#64748b" strokeDasharray="4 2" strokeWidth={1.25}
							dot={false} activeDot={false} />
					) : (
						<Line type="monotone" dataKey="avgBaseline"
							name="Media"
							stroke="#64748b" strokeDasharray="4 2" strokeWidth={1.25}
							dot={false} activeDot={false} />
					)}

					{/* Valor real — linha grossa colorida */}
					<Line
						type="monotone"
						dataKey="kpiValue"
						name="Valor real"
						stroke={'url(#lineGrad-' + incident.incidentId + ')'}
						strokeWidth={2.5}
						dot={{ r: 3, fill: techColor, strokeWidth: 0 }}
						activeDot={{ r: 5, fill: techColor, stroke: 'white', strokeWidth: 2 }}
					/>
				</ComposedChart>
			</ResponsiveContainer>
		</Box>
	);
}